import React, { useMemo, useState } from "react";
import Button from "components/ui/Button";
import Modal from "components/ui/Modal";
import MessageChanges from "components/commsLog/MessageChanges";
import MessageEditor from "components/commsLog/MessageEditor";
import PropertyPicker from "components/commsLog/PropertyPicker";
import {
  useCommsTemplatesQuery,
  useManualSendCommsMessage,
  usePreviewCommsMessage,
} from "apis/commsLog";
import {
  formatSydney,
  skipReasons,
  templateStateGroups,
} from "utils/commsLogStates";

const sydneyDate = (value) =>
  new Date(value).toLocaleDateString("en-AU", {
    timeZone: "Australia/Sydney",
    weekday: "short",
    day: "numeric",
    month: "short",
  });

const PINNED_TEMPLATE_NAMES = new Set([
  "LTM_APPROVED_LINK",
  "LTM_DIGITAL_AUTO_SEND",
]);

const Panel = ({ tone = "muted", title, children }) => {
  const tones = {
    muted: "bg-lightgray text-darkergray",
    warn: "bg-orange-50 text-orange-800",
    bad: "bg-red-50 text-red-700",
    good: "bg-green-50 text-green-800",
  };
  return (
    <div className={`rounded-md p-3 ${tones[tone]}`}>
      {title ? <p className="text-xs uppercase tracking-wide">{title}</p> : null}
      <div className="text-sm">{children}</div>
    </div>
  );
};

const ManualTrigger = ({ property, organizationId, onClose }) => {
  const [chosen, setChosen] = useState(property || null);
  const [templateName, setTemplateName] = useState("");
  const [showCode, setShowCode] = useState(false);
  const [result, setResult] = useState(null);
  const [text, setText] = useState("");
  const [blockDraft, setBlockDraft] = useState([]);
  const [confirming, setConfirming] = useState(false);

  const { templates, unsent, isPending: templatesPending } =
    useCommsTemplatesQuery(chosen?.propertyId);
  const preview = usePreviewCommsMessage();
  const manualSend = useManualSendCommsMessage();

  const template = useMemo(
    () => templates.find((item) => item.name === templateName),
    [templates, templateName],
  );

  // Grouped so the admin reads why a message is where it is before picking it.
  const groups = useMemo(
    () => {
      const pinnedRows = templates
        .filter((item) => PINNED_TEMPLATE_NAMES.has(item.name))
        .sort((a, b) => a.label.localeCompare(b.label));

      const stateGroups = templateStateGroups
        .map((group) => ({
          ...group,
          rows: templates
            .filter(
              (item) =>
                item.state === group.state && !PINNED_TEMPLATE_NAMES.has(item.name),
            )
            .sort((a, b) => a.label.localeCompare(b.label)),
        }))
        .filter((group) => group.rows.length);

      return pinnedRows.length
        ? [{ state: "ltm", heading: "LTM links", rows: pinnedRows }, ...stateGroups]
        : stateGroups;
    },
    [templates],
  );

  const target = useMemo(() => {
    if (result?.draft) {
      return {
        campaignId: result.draft.campaignId,
        messageId: result.draft.messageId,
        fresh: true,
      };
    }
    if (result?.existing && !result.existing.isSent) {
      return {
        campaignId: result.existing.campaignId,
        messageId: result.existing.messageId,
        fresh: false,
      };
    }
    return null;
  }, [result]);

  const run = (rebuildFor) => {
    setResult(null);
    setText("");
    setBlockDraft([]);
    setShowCode(false);
    preview.mutate(
      {
        propertyId: chosen.propertyId,
        templateName,
        ...(rebuildFor ? { rebuildFor } : {}),
      },
      {
        onSuccess: (response) => {
          const data = response?.data?.data || null;
          setResult(data);
          const source = data?.draft || (data?.existing?.isSent ? null : data?.existing);
          setText(source?.text || source?.message || "");
          setBlockDraft(source?.editable || []);
        },
      },
    );
  };

  const sourceBlocks =
    result?.draft?.blocks || (result?.existing?.isSent ? null : result?.existing?.blocks) || [];
  // The vendor group renders the blocks, so the words in them are what gets edited.
  const editsBlocks = blockDraft.length > 0;

  const send = () =>
    manualSend.mutate(
      {
        campaignId: target.campaignId,
        messageId: target.messageId || undefined,
        templateName,
        text,
        blocks: sourceBlocks,
        ...(editsBlocks ? { textBlocks: blockDraft } : {}),
      },
      {
        onSuccess: () => {
          setConfirming(false);
          onClose();
        },
      },
    );

  const canSend = Boolean(
    target && (editsBlocks ? blockDraft.some((value) => value.trim()) : text.trim()),
  );
  const missing = (result?.missingRecords || []).filter((check) => !check.ok);
  const alreadyWritten =
    result?.reason === "alreadyGenerated" && !result.existing?.isSent;

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={
        chosen
          ? `Trigger a message · ${chosen.address || "Property"}`
          : "Trigger a message"
      }
      className="max-w-4xl"
    >
      {!chosen ? (
        <PropertyPicker organizationId={organizationId} onPick={setChosen} />
      ) : (
        <div className="space-y-4">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <p className="text-sm text-darkgray">
              Pick a message and this runs the real automation for this property,
              exactly as the schedule would. Nothing reaches the vendors until you
              press Send.
            </p>
            {property ? null : (
              <button
                type="button"
                className="text-xs text-darkgray underline"
                onClick={() => {
                  setChosen(null);
                  setTemplateName("");
                  setResult(null);
                  setText("");
                  setBlockDraft([]);
                }}
              >
                Change property
              </button>
            )}
          </div>

          {unsent.orphaned.length ? (
            <Panel tone="bad" title="These will not go out on their own">
              <ul className="mt-1 space-y-1">
                {unsent.orphaned.map((row) => (
                  <li key={String(row.messageId)}>
                    {row.label} — written {formatSydney(row.createdAt)}, with no send time.
                  </li>
                ))}
              </ul>
              <p className="mt-2 text-xs">
                Pick it below and send it from here.
              </p>
            </Panel>
          ) : null}

          {unsent.waitingOnBeleef.length || unsent.waitingOnAgent.length ? (
            <Panel title="Already written, not sent yet">
              {unsent.waitingOnBeleef.length ? (
                <p>
                  {unsent.waitingOnBeleef.length} waiting on a Beleef admin to clear
                  them.
                </p>
              ) : null}
              {unsent.waitingOnAgent.length ? (
                <p>
                  {unsent.waitingOnAgent.length} cleared and sitting with the agent for
                  approval.
                </p>
              ) : null}
              <p className="mt-1 text-xs">
                These are moving normally — nothing is broken about them.
              </p>
            </Panel>
          ) : null}

          <div className="flex flex-wrap items-end gap-3">
            <label className="flex flex-col gap-1">
              <span className="text-xs text-darkgray">Message</span>
              <select
                className="min-w-[420px] rounded-md border border-mediumgray px-3 py-2 text-sm"
                value={templateName}
                onChange={(event) => {
                  setTemplateName(event.target.value);
                  setResult(null);
                  setText("");
                  setBlockDraft([]);
                }}
              >
                <option value="">
                  {templatesPending ? "Loading…" : "Choose a message"}
                </option>
                {groups.map((group) => (
                  <optgroup key={group.state} label={group.heading}>
                    {group.rows.map((item) => (
                      <option key={item._id} value={item.name}>
                        {item.label}
                        {item.sentAt ? ` — ${sydneyDate(item.sentAt)}` : ""}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </select>
            </label>

            <Button
              className="rounded-md bg-black px-4 py-2 text-sm text-white disabled:opacity-40"
              disabled={!templateName || preview.isPending}
              loading={preview.isPending}
              onClick={() => run(null)}
            >
              {template && !template.canGenerate ? "Open it" : "Generate"}
            </Button>
          </div>

          {template && !result ? (
            <p className="text-xs text-darkgray">
              {template.canGenerate
                ? template.approval
                  ? "This one normally waits for the agent to approve it."
                  : ""
                : "The schedule never writes this one, so it can only be opened and sent if something else already wrote it."}
            </p>
          ) : null}

          {preview.isPending ? (
            <Panel title="Running">
              Running the real scheduled message for {chosen.address}. One to three
              minutes — it loads the schedule file and writes with the same model the
              automation uses. Nothing is saved yet.
            </Panel>
          ) : null}

          {result?.generated ? (
            <Panel tone="good" title="Ready">
              This is what the automation would have sent.
              {result.rebuiltFor
                ? ` Built as it would have been on ${sydneyDate(result.rebuiltFor)}.`
                : ""}
              {template?.approval
                ? " Sending it here skips the agent's approval."
                : ""}
            </Panel>
          ) : null}

          {result && !result.generated ? (
            <Panel
              tone={
                alreadyWritten
                  ? "good"
                  : result.reason === "alreadyGenerated"
                    ? "muted"
                    : "warn"
              }
              title={
                alreadyWritten
                  ? "Already written"
                  : result.reason === "alreadyGenerated"
                    ? "Already sent"
                    : "Why it did not generate"
              }
            >
              <p>
                {alreadyWritten
                  ? "This message is already written and was never sent. Edit it below and send it."
                  : result.plain || result.summary}
              </p>
              {result.slot?.skipReason ? (
                <p className="mt-1">{skipReasons[result.slot.skipReason]}</p>
              ) : null}
              {result.nextStep ? (
                <p className="mt-1">{result.nextStep}</p>
              ) : null}
              {missing.length ? (
                <ul className="mt-2 space-y-0.5 text-xs">
                  {missing.map((check) => (
                    <li key={check.name}>
                      Missing: {check.name} — {check.detail}
                    </li>
                  ))}
                </ul>
              ) : null}
            </Panel>
          ) : null}

          {/* Only when a specific past day is the reason; anything else, a date cannot help. */}
          {result?.canRebuild ? (
            <div className="rounded-md border border-black p-3">
              <p className="text-sm">
                It was due on <strong>{sydneyDate(result.dueAt)}</strong>
                {result.dueLabel ? ` — ${result.dueLabel}` : ""}. The schedule only ever
                runs it on that day, so it can only be built back to then.
              </p>
              <Button
                className="mt-2 rounded-md bg-black px-4 py-2 text-sm text-white"
                loading={preview.isPending}
                onClick={() => run(result.dueAt)}
              >
                Build it as it would have been on {sydneyDate(result.dueAt)}
              </Button>
            </div>
          ) : null}

          {result?.dueAt && !result.canRebuild ? (
            <p className="text-sm text-darkgray">
              It was due on {sydneyDate(result.dueAt)}, too far back to rebuild.
            </p>
          ) : null}

          {target ? (
            <div className="space-y-3">
              <MessageEditor
                blocks={sourceBlocks}
                text={text}
                values={blockDraft}
                onChangeText={setText}
                onChangeValues={setBlockDraft}
              />

              {result.rebuildsAtSend ? (
                <p className="text-xs text-darkgray">
                  The images and buttons on this one are rebuilt fresh when it sends.
                </p>
              ) : null}

              {result.draft?.contextCheck ? (
                <MessageChanges
                  originalMessage={result.draft.originalMessage}
                  currentMessage={editsBlocks ? blockDraft.join("\n\n") : text}
                  contextCheck={result.draft.contextCheck}
                />
              ) : null}

              <Button
                className="rounded-md bg-black px-4 py-2 text-sm text-white disabled:opacity-40"
                disabled={!canSend}
                onClick={() => setConfirming(true)}
              >
                Send to the vendor group
              </Button>
            </div>
          ) : null}

          {result?.existing?.isSent ? (
            <div className="space-y-2">
              <Panel title="What already went out">
                <p className="whitespace-pre-wrap">{result.existing.message}</p>
              </Panel>
              <p className="text-xs text-darkgray">
                Sent {formatSydney(result.existing.sentAt)}. Use the Messages tab to send
                it again or retract it.
              </p>
            </div>
          ) : null}

          {result?.logs?.length ? (
            <Panel tone="bad" title="Errors while it ran">
              <ul className="space-y-1">
                {result.logs.map((line) => (
                  <li key={line} className="font-mono text-xs">
                    {line}
                  </li>
                ))}
              </ul>
            </Panel>
          ) : null}

          {/* An admin never needs this; it is the proof for whoever fixes the schedule. */}
          {result?.guard ? (
            <div>
              <button
                type="button"
                className="text-xs text-darkgray underline"
                onClick={() => setShowCode((value) => !value)}
              >
                {showCode
                  ? "Hide the line that stopped it"
                  : "Show the line that stopped it (for engineers)"}
              </button>
              {showCode ? (
                <div className="mt-2 rounded-md bg-darkergray p-3">
                  <p className="text-xs uppercase tracking-wide text-white">
                    privateTreaty.js · {result.generatorName}() · line{" "}
                    {result.guard.line}
                  </p>
                  <pre className="mt-2 overflow-x-auto text-xs text-white">
                    {result.guard.snippet
                      .map((line) => `${String(line.line).padStart(6)}  ${line.text}`)
                      .join("\n")}
                  </pre>
                  {result.conditions?.length ? (
                    <ul className="mt-2 list-disc pl-5 text-xs text-white">
                      {result.conditions.map((condition) => (
                        <li key={condition}>{condition}</li>
                      ))}
                    </ul>
                  ) : null}
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
      )}

      {confirming ? (
        <Modal
          isOpen
          onClose={() => setConfirming(false)}
          title="Send this to the vendors?"
          className="max-w-lg"
        >
          <p className="text-sm">
            This goes into the live vendor group for {chosen.address} now, and the
            vendors are notified.
            {target?.fresh
              ? " It was built manually, so it has not been through the agent's approval."
              : " This message was already written but never sent."}
          </p>
          <div className="mt-4 flex justify-end gap-2">
            <button
              type="button"
              className="rounded-md border border-mediumgray px-4 py-2 text-sm"
              onClick={() => setConfirming(false)}
            >
              Cancel
            </button>
            <Button
              className="rounded-md bg-black px-4 py-2 text-sm text-white"
              loading={manualSend.isPending}
              onClick={send}
            >
              Yes, send it now
            </Button>
          </div>
        </Modal>
      ) : null}
    </Modal>
  );
};

export default ManualTrigger;
